import React, { useState } from 'react';
import { usePlatformsStore, useUIStore, useSubscriptionStore } from '../stores';
import {
  Twitter,
  Instagram,
  Linkedin,
  Facebook,
  Youtube,
  Music,
  Pin,
  AtSign,
  Check,
  X,
  Loader2,
  ExternalLink,
  AlertCircle,
  Zap,
  Shield,
  RefreshCw,
} from 'lucide-react';

const platformDetails = {
  twitter: {
    name: 'Twitter',
    icon: Twitter,
    color: '#1DA1F2',
    bgColor: 'bg-[#1DA1F2]/10',
    description: 'Post tweets, threads, and engage with your audience',
    features: ['Tweets', 'Threads', 'Polls', 'Analytics'],
  },
  instagram: {
    name: 'Instagram',
    icon: Instagram,
    color: '#E4405F',
    bgColor: 'bg-[#E4405F]/10',
    description: 'Share photos, reels, and stories',
    features: ['Feed Posts', 'Reels', 'Stories', 'Carousels'],
  },
  facebook: {
    name: 'Facebook',
    icon: Facebook,
    color: '#1877F2',
    bgColor: 'bg-[#1877F2]/10',
    description: 'Manage your Facebook pages and groups',
    features: ['Page Posts', 'Groups', 'Stories', 'Events'],
  },
  linkedin: {
    name: 'LinkedIn',
    icon: Linkedin,
    color: '#0A66C2',
    bgColor: 'bg-[#0A66C2]/10',
    description: 'Share professional content and articles',
    features: ['Posts', 'Articles', 'Documents', 'Polls'],
  },
  tiktok: {
    name: 'TikTok',
    icon: Music,
    color: '#000000',
    bgColor: 'bg-gray-100 dark:bg-gray-800',
    description: 'Upload and schedule TikTok videos',
    features: ['Videos', 'Duets', 'Sounds', 'Effects'],
  },
  youtube: {
    name: 'YouTube',
    icon: Youtube,
    color: '#FF0000',
    bgColor: 'bg-[#FF0000]/10',
    description: 'Manage your YouTube channel content',
    features: ['Videos', 'Shorts', 'Community', 'Premieres'],
  },
  pinterest: {
    name: 'Pinterest',
    icon: Pin,
    color: '#E60023',
    bgColor: 'bg-[#E60023]/10',
    description: 'Create and schedule pins to your boards',
    features: ['Pins', 'Idea Pins', 'Boards', 'Rich Pins'],
  },
  threads: {
    name: 'Threads',
    icon: AtSign,
    color: '#000000',
    bgColor: 'bg-gray-100 dark:bg-gray-800',
    description: 'Share text updates and join conversations',
    features: ['Threads', 'Replies', 'Reposts', 'Quotes'],
  },
};

export default function Integrations() {
  const [connecting, setConnecting] = useState(null);
  const [disconnecting, setDisconnecting] = useState(null);
  
  const { platforms, connectPlatform, disconnectPlatform } = usePlatformsStore();
  const { addToast } = useUIStore();
  const { plan, getCurrentFeatures } = useSubscriptionStore();
  
  const features = getCurrentFeatures();
  const connectedCount = platforms.filter(p => p.connected).length;
  const maxPlatforms = features.platforms;
  
  const handleConnect = async (platformId) => {
    if (connectedCount >= maxPlatforms) {
      addToast({
        type: 'error',
        title: 'Upgrade required',
        message: `Your ${plan} plan allows ${maxPlatforms} platforms. Upgrade to connect more.`,
      });
      return;
    }

    setConnecting(platformId);

    const result = await connectPlatform(platformId);

    if (result.success && result.oauthUrl) {
      addToast({
        type: 'success',
        title: 'Connecting...',
        message: `Opening ${platformDetails[platformId]?.name || platformId} authorization window.`,
      });
    } else {
      addToast({
        type: 'error',
        title: 'Connection failed',
        message: result.error || `Could not connect ${platformDetails[platformId]?.name || platformId}. Platform integration coming soon.`,
      });
    }

    setConnecting(null);
  };
  
  const handleDisconnect = async (platformId) => {
    setDisconnecting(platformId);
    
    await disconnectPlatform(platformId);
    
    addToast({
      type: 'info',
      title: 'Disconnected',
      message: `${platformDetails[platformId].name} has been disconnected.`,
    });
    
    setDisconnecting(null);
  };
  
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Integrations</h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">
          Connect your social media accounts to start scheduling
        </p>
      </div>
      
      {/* Status bar */}
      <div className="card p-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-full bg-brand-100 dark:bg-brand-900/30 flex items-center justify-center">
              <Zap className="w-5 h-5 text-brand-600 dark:text-brand-400" />
            </div>
            <div>
              <p className="font-medium text-gray-900 dark:text-white">
                {connectedCount} of {maxPlatforms} platforms connected
              </p>
              <p className="text-sm text-gray-500">
                {plan === 'free' ? 'Upgrade for more platforms' : `${plan} plan`}
              </p>
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <div className="h-2 w-32 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div 
              className="h-full bg-brand-500 rounded-full transition-all"
              style={{ width: `${(connectedCount / maxPlatforms) * 100}%` }}
            />
          </div>
        </div>
      </div>
      
      {/* Platforms grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {platforms.map((platform) => {
          const details = platformDetails[platform.id];
          const Icon = details.icon;
          const isConnecting = connecting === platform.id;
          const isDisconnecting = disconnecting === platform.id;
          
          return (
            <div key={platform.id} className="card p-5">
              <div className="flex items-start gap-4">
                <div 
                  className={`p-3 rounded-xl ${details.bgColor}`}
                  style={{ color: details.color }}
                >
                  <Icon className="w-6 h-6" />
                </div>
                
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-gray-900 dark:text-white">
                      {details.name}
                    </h3>
                    {platform.connected ? (
                      <span className="flex items-center gap-1 text-sm text-green-600 dark:text-green-400">
                        <Check className="w-4 h-4" />
                        Connected
                      </span>
                    ) : (
                      <span className="text-sm text-gray-400">Not connected</span>
                    )}
                  </div>
                  
                  <p className="text-sm text-gray-500 mt-1">
                    {details.description}
                  </p>
                  
                  {/* Features */}
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {details.features.map((feature, i) => (
                      <span 
                        key={i}
                        className="text-xs px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400"
                      >
                        {feature}
                      </span>
                    ))}
                  </div>
                  
                  {/* Connected info */}
                  {platform.connected && platform.username && (
                    <div className="mt-3 p-2 rounded-lg bg-gray-50 dark:bg-gray-800 flex items-center justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {platform.username}
                      </span>
                      <button 
                        className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1"
                      >
                        <RefreshCw className="w-3 h-3" />
                        Refresh
                      </button>
                    </div>
                  )}
                  
                  {/* Actions */}
                  <div className="mt-4 flex items-center gap-2">
                    {platform.connected ? (
                      <button
                        onClick={() => handleDisconnect(platform.id)}
                        disabled={isDisconnecting}
                        className="btn-secondary text-sm py-1.5 px-3 text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                      >
                        {isDisconnecting ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-1.5" />
                        ) : (
                          <X className="w-4 h-4 mr-1.5" />
                        )}
                        Disconnect
                      </button>
                    ) : (
                      <button
                        onClick={() => handleConnect(platform.id)}
                        disabled={isConnecting || connectedCount >= maxPlatforms}
                        className="btn-primary text-sm py-1.5 px-3"
                      >
                        {isConnecting ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-1.5" />
                        ) : (
                          <ExternalLink className="w-4 h-4 mr-1.5" />
                        )}
                        Connect
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      
      {/* Security note */}
      <div className="card p-4 flex items-start gap-3 bg-gray-50 dark:bg-gray-800/50">
        <Shield className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-medium text-gray-900 dark:text-white">Your accounts are secure</p>
          <p className="text-sm text-gray-500 mt-1">
            We use OAuth 2.0 for secure authentication. We never store your passwords and you can revoke access anytime.
          </p>
        </div>
      </div>
    </div>
  );
}
